// Provides a common interface for TTS, utterance audio,
// or scene audio (with utterance sprites)
//
import stampit from 'stampit'
import Voice from './voice'
import Utterance from './utterance'
import EventEmitter from 'events'
import {Howler, Howl} from 'howler'

//
//  utterance: {pitch: 1, rate: 1, delay: 0,
//        audio: {url: 'http://url.mp3', spriteName: 'spritename_for_scene_howler'}
//  }

// Howler plays audio on mobile that gets stalled on the first touch up event.
// We turn this off because we handle this case ourselves and try to be consistent
// with TTS.  Not setting this to false can cause an "echo" as the audio can be
// played twice
Howler.mobileAutoEnable = false

const Play = stampit({
  props: {
    utterance: null,
    msg: null, // SpeechSynthesis Utterance
    sceneHowl: null, //
    randomPlayId: null,
    eventEmitter: null,
    spriteName: null,
    speakingDurationEstimate: 0,
    ttsStartTime: 0,
    bIsPlaying: false // howler playing function busted on Safari reload
  },
  statics: {
    playingId: 0, // Helps determine if we have "stalled" due to mobile.
    staticEventEmitter: new EventEmitter()
  },
  init ({utterance, sceneHowl, voice, maxDelay, playOnLoad}) {
    this.bIsPlaying = false
    this.randomPlayId = Math.random()
    this.eventEmitter = new EventEmitter()
    this.speakingDurationEstimate = 0
    if (!utterance) {
      throw new Error('Invalid utterance arguement to Play init')
    }
    this.utterance = utterance
    this.spriteName = null
    if (sceneHowl && utterance.audio && utterance.audio.spriteName) {
      this.howl = sceneHowl
      this.spriteName = utterance.audio.spriteName
    } else if (utterance.audio && utterance.audio.url) {
      this.howl = new Howl({
        src: [utterance.audio.url],
        preload: false
      })
    }
    if (this.howl) {
      var setDuration = () => {
        if (this.howl) {
          if (this.spriteName) {
            // Google generously pads the audio at the end with about 320 ms
            this.speakingDurationEstimate = this.howl.duration(this.spriteName) * 1000 - 320
          }
          // Google generously pads the audio at the end with about 320 ms
          this.speakingDurationEstimate = this.howl.duration() * 1000 - 320
        }
      }
      // If the howl isn't loaded, we'll get zero, but if it as been loaded then onload
      // won't be called and this is out chance
      setDuration()
      // Fires when the sound finishes playing.
      this.howl.on('end', () => {
        this.eventEmitter.emit('end')
      })
      this.howl.on('play', () => {
        this.bIsPlaying = true
        this.eventEmitter.emit('start', this.speakingDurationEstimate)
      })
      this.howl.on('load', () => {
        setDuration()
      })
      this.howl.on('loaderror', () => {
        console.warn('Howler load error')
      })
      this.howl.on('playerror', () => {
        console.warn('Howler play error')
      })
      this.howl.on('stop', () => {
        this.bIsPlaying = false
      })
      this.howl.on('pause', () => {
        this.bIsPlaying = false
      })
      this.howl.on('end', () => {
        this.bIsPlaying = false
      })
      this.howl.on('unlock', () => {
        console.warn('Howler unlock')
      })
      this.howl.load()
    } else if (voice) {
      if (!this.utterance.text) {
        return
      }
      var text = Utterance(this.utterance).getProcessedText()
      if (text.length === 0) {
        return
      }
      this.msg = new SpeechSynthesisUtterance()

      if (this.utterance.ttsDuration) {
        this.speakingDurationEstimate = this.utterance.ttsDuration
      } else {
        // if you change the rate, you would have to adjust
        this.speakingDurationEstimate = text.length * 50

        // Chinese needs a different calculation.  Haven't tried other Asian languages.
        if (text.match(/[\u3400-\u9FBF]/)) {
          this.speakingDurationEstimate = this.utterance.text.length * 200
        }

        // Russian needs a different calculation.
        if (text.match(/[\u0400-\u04FF]/)) {
          this.speakingDurationEstimate = this.utterance.text.length * 80
        }
      }

      this.msg.rate = this.utterance.rate
      this.msg.pitch = this.utterance.pitch
      this.msg.text = text

      this.msg.delay = this.utterance.delay || 0
      // Add delay as paramater for ease of use
      if (maxDelay && this.utterance.delay && this.utterance.delay > maxDelay) {
        this.msg.delay = maxDelay
      }

      this.msg.voice = Voice.getTTSVoice(voice)
      if (this.msg.voice) {
        this.msg.voiceURI = this.msg.voice.voiceURI
        // this.msg.lang appears to be required for my samsung galaxy tab 4 to play TTS in non-english
        // note it stores locale as en_US instead of en-us
        this.msg.lang = this.msg.voice.lang
      }
      this.msg.addEventListener('start', () => {
        var now = new Date()
        this.ttsStartTime = now.getTime()
        this.eventEmitter.emit('start', this.speakingDurationEstimate)
      })
      this.msg.addEventListener('end', () => {
        var now = new Date()
        var duration = now.getTime() - this.ttsStartTime
        if (duration > 0 && duration < 100000) {
          if (!this.utterance.ttsDuration || Math.abs(duration - this.utterance.ttsDuration) > 100) {
            Play.staticEventEmitter.emit('updatedSpeakingDurationEstimate', this.speakingDurationEstimate)
          }
          this.utterance.ttsDuration = duration
        }
        this.eventEmitter.emit('end')
      })
      if (playOnLoad) {
        this.play()
      }
    }
  },
  methods: {
    playIfRandomIdMatches () {
      if (this.randomPlayId === Play.playingId) {
        if (this.howl) {
          if (this.howl.state() !== 'loaded') {
            this.howl.once('load', () => {
              this.howl.play()
              this.checkIfStalled()
            })
          } else {
            if (this.spriteName) {
              this.howl.play(this.spriteName)
            } else {
              this.howl.play()
            }
            this.checkIfStalled()
          }
        } else if (this.msg) {
          speechSynthesis.speak(this.msg)
          this.checkIfStalled()
        }
      } else {
        console.warn('random id doesnt match')
      }
    },
    isPlaying () {
      var bTTS = false
      var bAudio = false
      if (this.howl) {
        // bAudio = this.howl.playing()  <- not working on Safari desktop reload
        return this.bIsPlaying
      } else if (speechSynthesis) {
        return speechSynthesis.speaking
      }
      return bTTS || bAudio
    },
    checkIfStalled () {
      setTimeout(() => {
        if (!this.isPlaying()) {
          setTimeout(() => {
            if (!this.isPlaying() && Play.playingId === this.randomPlayId) {
              // Used to test if we are actually speaking or if the platform ignored us
              this.eventEmitter.emit('stalled')
              if (this.howl) {
                // If I don't stop, Howler tries to play on click, and we already handle this
                // Even with Howl.mobileAutoEnable = false.  This can result in an "echo" as the
                // audio is played twice.
                // console.log('stopping howler because we are stalled')
                this.howl.pause()
              }
            }
          }, 100)
        }
      }, 0)
    },
    play () {
      if (!this.howl && !this.msg) {
        console.warn('nothing to play')
        return
      }
      Play.playingId = this.randomPlayId
      // The delay parameter is added to the SpeechSynthesisUtterance object
      if (this.utterance.delay > 0) {
        setTimeout(() => {
          this.playIfRandomIdMatches()
        }, this.utterance.delay * 1000)
      } else {
        this.playIfRandomIdMatches()
      }
    },
    stop () {
      if (speechSynthesis.speaking) {
        speechSynthesis.cancel()
      }
      if (this.howl) {
        this.howl.stop()
      }
    }
  }
})
export default Play



// WEBPACK FOOTER //
// ./src/tts/play.js